<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-message-text-outline</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="subtitle-1 font-weight-bold">Feedback Inbox</div>
          <div class="caption grey--text">Notes from your managers &amp; colleagues</div>
        </div>
        <v-btn icon small :loading="loading" @click="load" title="Refresh">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Inbox label -->
      <div class="d-flex align-center mb-3">
        <v-icon color="primary" class="mr-2">mdi-inbox</v-icon>
        <span class="body-2 font-weight-bold">Inbox</span>
        <v-badge
          v-if="unread > 0"
          :content="String(unread)"
          color="error"
          inline
          class="ml-2"
        />
      </div>

      <!-- Feedback items -->
      <div
        v-for="note in inbox"
        :key="note.id"
        class="pa-3 rounded-lg mb-3"
        :style="note.read
          ? 'background:#f5f5f5;border:1px solid #e0e0e0;'
          : 'background:#fff8f0;border:1px solid #ffe0b2;'"
      >
        <!-- Top row: relationship chip + role chips + from + date + mark read -->
        <div class="d-flex align-center mb-2" style="flex-wrap:wrap;gap:6px;">
          <v-chip
            x-small
            label
            :color="note.relationship === 'manager' ? 'blue lighten-4' : 'grey lighten-3'"
            :class="note.relationship === 'manager' ? 'blue--text text--darken-2 font-weight-bold' : 'font-weight-medium'"
          >
            <v-icon x-small class="mr-1">
              {{ note.relationship === 'manager' ? 'mdi-account-tie' : 'mdi-account-group' }}
            </v-icon>
            {{ note.relationshipLabel }}
          </v-chip>
          <v-chip
            v-for="r in note.managedRoles"
            :key="note.id + '-role-' + r.id"
            x-small
            outlined
            color="blue darken-1"
            class="font-weight-medium"
            title="Sender shares this role with you"
          >
            {{ r.name }}
          </v-chip>
          <v-chip
            v-if="note.teamName"
            x-small
            color="deep-purple lighten-5"
            class="deep-purple--text text--darken-2 font-weight-medium"
          >
            <v-icon x-small class="mr-1">mdi-account-group</v-icon>
            {{ note.teamName }}
          </v-chip>
          <v-chip
            v-if="note.category && note.category !== 'General'"
            x-small
            outlined
            color="grey darken-1"
          >
            {{ note.category }}
          </v-chip>
          <span
            v-if="!note.read"
            style="width:8px;height:8px;border-radius:50%;background:#f57c00;display:inline-block;flex-shrink:0;"
          />
          <span class="caption grey--text flex-grow-1">
            From {{ note.from }} &bull; {{ formatDate(note.date) }}
          </span>
          <v-btn
            v-if="!note.read"
            x-small
            text
            color="primary"
            class="text-capitalize pa-0 font-weight-medium"
            style="min-width:0;"
            @click="markRead(note)"
          >
            Mark read
          </v-btn>
        </div>

        <!-- Message -->
        <div class="body-2">{{ note.message }}</div>
      </div>

      <div v-if="inbox.length === 0" class="caption grey--text text-center py-4">
        No feedback yet.
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'FeedbackInboxCard',
  data() {
    return {
      loading: false,
      inbox: [],
    };
  },
  computed: {
    unread() {
      return this.inbox.filter(n => !n.read).length;
    },
  },
  mounted() {
    this.load();
    this.$root.$on('growth:feedback:sent', this.load);
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:feedback:sent', this.load);
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    normalizeNote(n) {
      // relationship is derived on the backend from user_role_members:
      // - 'manager'   → sender shares one of the recipient's roles
      // - 'colleague' → no shared role
      // managed_roles is the list of roles the sender & recipient have in common
      // (what the manager-label applies to for a multi-role user).
      const rel = n.relationship || 'colleague';
      const managedRoles = Array.isArray(n.managed_roles) ? n.managed_roles : [];
      return {
        id: n.id,
        from: n.sender?.name || 'Someone',
        relationship: rel,
        relationshipLabel: rel === 'manager'
          ? (managedRoles.length === 1
              ? `Manager · ${managedRoles[0].name}`
              : managedRoles.length > 1
                ? `Manager · ${managedRoles.length} roles`
                : 'Manager')
          : 'Colleague',
        managedRoles,
        teamName: n.role?.name || null,
        category: n.category || 'General',
        date: n.created_at,
        message: n.message,
        read: !!n.is_read,
      };
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/feedback/inbox');
        if (res.data.response) this.inbox = res.data.data.map(this.normalizeNote);
      } finally {
        this.loading = false;
      }
    },
    async markRead(note) {
      try {
        await this.$axios.post(`api/employee-growth/feedback/${note.id}/read`);
        note.read = true;
      } catch (e) { note.read = true; /* optimistic */ }
    },
    formatDate(iso) {
      // $date/$tz so this follows the display timezone and clock settings.
      const d = this.$date(iso);
      if (!d.isValid()) return iso;
      return d.format(`MMM D, YYYY ${this.$tz.time}`);
    },
  },
};
</script>
